import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import { brands, brandCampaigns } from "@/mocks/brandData";
import type { BrandIPAsset } from "@/mocks/brandData";

export default function BrandDetailPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const brand = brands.find((b) => b.id === brandId);
  const campaigns = brandCampaigns.filter((c) => c.brandId === brandId);

  const [selectedAsset, setSelectedAsset] = useState<BrandIPAsset | null>(null);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);

  if (!brand) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <Navbar />
        <div className="text-center pt-20">
          <p className="text-white/40 text-lg mb-4">브랜드를 찾을 수 없습니다.</p>
          <Link to="/brands" className="text-[#f5e642] hover:underline cursor-pointer">브랜드 목록으로</Link>
        </div>
      </div>
    );
  }

  const handleCopyTag = (tag: string) => {
    navigator.clipboard.writeText(tag).catch(() => {});
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 2000);
  };

  const totalBonus = brand.ipAssets.reduce((sum, a) => sum + a.tokenBonus, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero */}
      <div className="relative h-64 overflow-hidden">
        <img src={brand.coverImage} alt={brand.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/60 via-transparent to-[#0a0a0f]/60" />
      </div>

      {/* Brand Header */}
      <div className="max-w-7xl mx-auto px-6 -mt-16 relative z-10 mb-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5">
          <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 flex-shrink-0" style={{ borderColor: `${brand.accentColor}50` }}>
            <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-white font-black text-2xl">{brand.name}</h1>
              {brand.verified && <i className="ri-verified-badge-fill text-lg" style={{ color: brand.accentColor }} />}
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${brand.accentColor}20`, color: brand.accentColor }}>
                {brand.categoryLabel}
              </span>
            </div>
            <p className="text-white/50 text-sm">{brand.tagline}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-4 py-2 rounded-xl" style={{ background: `${brand.accentColor}20`, border: `1px solid ${brand.accentColor}40` }}>
              <i className="ri-coin-fill text-sm" style={{ color: brand.accentColor }} />
              <span className="font-black text-sm" style={{ color: brand.accentColor }}>+{brand.tokenReward} 토큰/사용</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main Content */}
          <div className="flex-1 space-y-8">

            {/* Brand Description */}
            <div className="rounded-2xl border border-white/10 bg-white/3 p-6">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-information-line text-sm" style={{ color: brand.accentColor }} />
                <h2 className="text-white font-bold text-sm">브랜드 소개</h2>
              </div>
              <p className="text-white/55 text-sm leading-relaxed">{brand.description}</p>

              <div className="grid grid-cols-3 gap-3 mt-5">
                {[
                  { label: "제공 IP 에셋", value: `${brand.ipAssets.length}개`, icon: "ri-image-line" },
                  { label: "총 활용 횟수", value: brand.totalUsage.toLocaleString(), icon: "ri-bar-chart-line" },
                  { label: "최대 보너스", value: `x${brand.bonusMultiplier}`, icon: "ri-flashlight-line" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl p-3 border border-white/8 bg-white/3 text-center">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg mx-auto mb-2" style={{ background: `${brand.accentColor}15` }}>
                      <i className={`${stat.icon} text-sm`} style={{ color: brand.accentColor }} />
                    </div>
                    <div className="font-black text-base text-white">{stat.value}</div>
                    <div className="text-white/30 text-[11px] mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* IP Assets */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-base">제공 IP 에셋</h2>
                <span className="text-white/30 text-xs">{brand.ipAssets.length}개 에셋</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {brand.ipAssets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => setSelectedAsset(asset)}
                    className="rounded-2xl border border-white/10 bg-white/3 overflow-hidden hover:border-white/20 transition-all cursor-pointer group"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img src={asset.image} alt={asset.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" />
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-lg" style={{ background: `${brand.accentColor}25`, border: `1px solid ${brand.accentColor}40` }}>
                        <i className="ri-coin-fill text-xs" style={{ color: brand.accentColor }} />
                        <span className="text-xs font-bold" style={{ color: brand.accentColor }}>+{asset.tokenBonus}</span>
                      </div>
                      <div className="absolute bottom-3 left-3">
                        <span className="px-2 py-1 rounded-md text-[10px] font-bold font-mono" style={{ background: `${brand.accentColor}30`, color: brand.accentColor, border: `1px solid ${brand.accentColor}40` }}>
                          {asset.tag}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold text-sm mb-1">{asset.name}</h3>
                      <p className="text-white/40 text-xs leading-relaxed mb-3 line-clamp-2">{asset.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white/30 text-xs">{asset.usageCount.toLocaleString()}회 활용</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopyTag(asset.tag); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                          style={{ background: `${brand.accentColor}20`, color: brand.accentColor, border: `1px solid ${brand.accentColor}30` }}
                        >
                          <i className={`${copiedTag === asset.tag ? "ri-check-line" : "ri-clipboard-line"} text-xs`} />
                          {copiedTag === asset.tag ? "복사됨!" : "태그 복사"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Campaigns */}
            {campaigns.length > 0 && (
              <div>
                <h2 className="text-white font-bold text-base mb-4">진행 중인 캠페인</h2>
                <div className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="rounded-2xl border border-white/10 bg-white/3 p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                          <img src={campaign.thumbnail} alt={campaign.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-bold text-sm">{campaign.title}</h3>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500/15 text-green-400 border border-green-500/25">진행 중</span>
                          </div>
                          <p className="text-white/40 text-xs leading-relaxed mb-2">{campaign.description}</p>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-white/30">{campaign.participantCount.toLocaleString()}명 참여</span>
                            <span className="text-white/20">·</span>
                            <span className="text-white/30">~{campaign.deadline}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-black text-lg" style={{ color: brand.accentColor }}>{campaign.tokenPerUse}</div>
                          <div className="text-white/30 text-[11px]">토큰/사용</div>
                        </div>
                      </div>
                      <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/8">
                        <p className="text-xs" style={{ color: brand.accentColor }}>
                          <i className="ri-gift-2-line mr-1.5" />
                          보너스 조건: {campaign.bonusCondition}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-5">

            {/* Token Reward Summary */}
            <div className="rounded-2xl border p-5" style={{ borderColor: `${brand.accentColor}30`, background: `${brand.accentColor}08` }}>
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">토큰 보상 구조</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-xs">IP 1개 사용 시</span>
                  <span className="font-bold text-sm" style={{ color: brand.accentColor }}>+{brand.tokenReward} 토큰</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-xs">최대 보너스 배율</span>
                  <span className="font-bold text-sm" style={{ color: brand.accentColor }}>x{brand.bonusMultiplier}</span>
                </div>
                <div className="h-px bg-white/10" />
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-xs">전체 IP 활용 시</span>
                  <span className="font-black text-base" style={{ color: brand.accentColor }}>+{totalBonus} 토큰</span>
                </div>
              </div>
            </div>

            {/* Usage Guidelines */}
            <div className="rounded-2xl border border-white/10 bg-white/3 p-5">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">IP 활용 가이드라인</h4>
              <div className="space-y-2.5">
                {[
                  { ok: true, text: "브랜드 IP를 긍정적인 맥락에서 활용해주세요" },
                  { ok: true, text: "제품/로고가 명확하게 보이도록 연출해주세요" },
                  { ok: true, text: "브랜드 아이덴티티와 어울리는 분위기를 유지해주세요" },
                  { ok: false, text: "경쟁 브랜드와 함께 노출하지 마세요" },
                  { ok: false, text: "브랜드 이미지를 훼손하는 내용에 사용하지 마세요" },
                ].map((g, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full"
                      style={g.ok ? { background: `${brand.accentColor}20` } : { background: "rgba(255,80,80,0.15)" }}>
                      <i className={`${g.ok ? "ri-check-line" : "ri-close-line"} text-[10px]`}
                        style={{ color: g.ok ? brand.accentColor : "#ff5050" }} />
                    </div>
                    <p className="text-white/45 text-xs leading-relaxed">{g.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick IP Tags */}
            <div className="rounded-2xl border border-white/10 bg-white/3 p-5">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-3">IP 태그 빠른 복사</h4>
              <div className="space-y-2">
                {brand.ipAssets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => handleCopyTag(asset.tag)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-white/8 bg-white/3 hover:border-white/20 transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                        <img src={asset.image} alt={asset.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-white/60 text-xs font-mono group-hover:text-white transition-colors">{asset.tag}</span>
                    </div>
                    <i className={`${copiedTag === asset.tag ? "ri-check-line text-green-400" : "ri-clipboard-line text-white/20 group-hover:text-white/50"} text-xs transition-colors`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedAsset(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg rounded-2xl border border-white/15 bg-[#0f0f18] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-56 overflow-hidden">
              <img src={selectedAsset.image} alt={selectedAsset.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f18] via-transparent to-transparent" />
              <button
                onClick={() => setSelectedAsset(null)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 border border-white/20 cursor-pointer"
              >
                <i className="ri-close-line text-white text-sm" />
              </button>
              <div className="absolute bottom-3 left-4">
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold font-mono" style={{ background: `${brand.accentColor}30`, color: brand.accentColor, border: `1px solid ${brand.accentColor}50` }}>
                  {selectedAsset.tag}
                </span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-black text-lg">{selectedAsset.name}</h3>
                  <p className="text-white/40 text-xs mt-0.5">{selectedAsset.usageCount.toLocaleString()}회 활용됨</p>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: `${brand.accentColor}20`, border: `1px solid ${brand.accentColor}40` }}>
                  <i className="ri-coin-fill text-sm" style={{ color: brand.accentColor }} />
                  <span className="font-black text-sm" style={{ color: brand.accentColor }}>+{selectedAsset.tokenBonus}</span>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4">{selectedAsset.description}</p>

              <div className="mb-4">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">활용 가이드라인</p>
                <div className="space-y-1.5">
                  {selectedAsset.guidelines.map((g, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full" style={{ background: `${brand.accentColor}20` }}>
                        <i className="ri-check-line text-[10px]" style={{ color: brand.accentColor }} />
                      </div>
                      <p className="text-white/45 text-xs leading-relaxed">{g}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleCopyTag(selectedAsset.tag)}
                className="w-full py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-all"
                style={{ background: `linear-gradient(135deg, ${brand.accentColor}, ${brand.accentColor}cc)`, color: "#0a0a0f" }}
              >
                <i className={`${copiedTag === selectedAsset.tag ? "ri-check-double-line" : "ri-clipboard-line"} text-base`} />
                {copiedTag === selectedAsset.tag ? "태그 복사 완료!" : `${selectedAsset.tag} 복사하기`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
